import { z } from 'zod'

import { ValidationError } from '../lib/errors'
import { titleCase } from '../lib/text'
import { keyBy } from '../lib/utils'
import { type ClinicId, clinicIdSchema } from './clinic'

export const urgencyIdSchema = z.enum(['high', 'medium', 'low'])
export type UrgencyId = z.infer<typeof urgencyIdSchema>

export const urgencyLevelSchema = z.object({
  id: urgencyIdSchema,
  label: z.string(),
  /** Minimum number of minutes before the appointment that it can be booked. */
  minimumBookingNoticeMinutes: z.number().int().nonnegative(),
})

export type UrgencyLevel = z.infer<typeof urgencyLevelSchema>

/**
 * Urgency levels for clinic bookings.
 * Controls how far in advance customers must book.
 * High = book now, medium = day+1, low = day+2.
 */
export const URGENCY_LEVELS = [
  { id: 'high', label: 'High', minimumBookingNoticeMinutes: 0 },
  { id: 'medium', label: 'Medium', minimumBookingNoticeMinutes: 1440 },
  { id: 'low', label: 'Low', minimumBookingNoticeMinutes: 2880 },
] as const satisfies UrgencyLevel[]

const appointmentTypeCategoryIds = [
  'wellness',
  'vaccs',
  'surgery',
  'grooming',
  'admin',
  'eol',
  'diagnostics',
  'telemedicine',
] as const

export type AppointmentTypeCategoryId = (typeof appointmentTypeCategoryIds)[number]
export type MemberBookingMode = 'self-service' | 'contact-team' | 'hidden'

export const appointmentTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  memberLabel: z.string().optional(),
  /** Optional description shown to the client when booking. */
  description: z.string(),
  /** Appointment duration in minutes. */
  duration: z.number().int().positive(),
  category: z.enum(appointmentTypeCategoryIds),
  memberBooking: z.enum(['self-service', 'contact-team', 'hidden']),
  /**
   * If set, this type may only be booked at these clinics (e.g. dental work that requires
   * specialised equipment only present at Bounds Green). Undefined means "available everywhere".
   */
  allowedClinicIds: z.array(clinicIdSchema).optional(),
})

export type AppointmentType = z.infer<typeof appointmentTypeSchema>

/**
 * All supported booking appointment types.
 * Hardcoded for now — they rarely change and don't need DB overhead.
 */
export const APPOINTMENT_TYPES = [
  {
    id: 'first-visit',
    name: 'First Visit',
    description:
      'An initial appointment to meet your pet, review their health history and discuss their care needs.',
    duration: 30,
    category: 'wellness',
    memberBooking: 'self-service',
    allowedClinicIds: ['islington', 'acton', 'london-fields', 'bounds-green', 'clifton'],
  },
  {
    id: 'post-op-consultation',
    name: 'Post Op Consultation',
    description: 'A follow-up to check your pet’s recovery after surgery.',
    duration: 10,
    category: 'surgery',
    memberBooking: 'self-service',
  },
  {
    id: 'pre-neuter-check',
    name: 'Pre Neuter Check',
    description: '',
    duration: 15,
    category: 'surgery',
    memberBooking: 'hidden',
  },
  {
    id: 'pre-op-dental',
    name: 'Pre Op Dental',
    description: '',
    duration: 15,
    category: 'surgery',
    memberBooking: 'hidden',
  },
  {
    id: 'rabies-vaccination',
    name: 'Rabies Vaccination',
    description: '',
    duration: 15,
    category: 'vaccs',
    memberBooking: 'hidden',
  },
  {
    id: 'recheck',
    name: 'Recheck',
    description: '',
    duration: 20,
    category: 'wellness',
    memberBooking: 'hidden',
  },
  {
    id: 'routine-checkup',
    name: 'Routine Checkup',
    memberLabel: 'Health check',
    description: 'A routine check of your pet’s general health when they are otherwise well.',
    duration: 20,
    category: 'wellness',
    memberBooking: 'self-service',
  },
  {
    id: 'sick-unwell',
    name: 'Sick / Unwell',
    description: '',
    duration: 20,
    category: 'wellness',
    memberBooking: 'hidden',
  },
  {
    id: 'weight-check-preventatives',
    name: 'Weight Check - Preventatives',
    description: 'A weight check for flea and worming treatment.',
    duration: 10,
    category: 'wellness',
    memberBooking: 'self-service',
  },
  {
    id: 'vaccinations',
    name: 'Vaccinations',
    description: 'An appointment for routine vaccinations, excluding rabies.',
    duration: 15,
    category: 'vaccs',
    memberBooking: 'self-service',
  },
  {
    id: 'injectable-treatments',
    name: 'Injectable Treatments',
    memberLabel: 'Injectable treatment (Cytopoint, Librela, Solensia)',
    description: '',
    duration: 15,
    category: 'vaccs',
    memberBooking: 'self-service',
  },
  {
    id: 'nail-clipping',
    name: 'Nail Clipping',
    description: '',
    duration: 10,
    category: 'grooming',
    memberBooking: 'self-service',
  },
  {
    id: 'anal-gland-expression',
    name: 'Anal Gland Expression',
    description: '',
    duration: 10,
    category: 'grooming',
    memberBooking: 'self-service',
  },
  {
    id: 'animal-health-certificate',
    name: 'Animal Health Certificate (AHC) for EU Travel',
    description: '',
    duration: 40,
    category: 'admin',
    memberBooking: 'contact-team',
  },
  {
    id: 'euthanasia',
    name: 'Euthanasia',
    description: '',
    duration: 60,
    category: 'eol',
    memberBooking: 'contact-team',
  },
  {
    id: 'book-on-day',
    name: 'Book On Day',
    description: '',
    duration: 20,
    category: 'wellness',
    memberBooking: 'hidden',
  },
  {
    id: 'neuter',
    name: 'Neuter',
    description: '',
    duration: 15,
    category: 'surgery',
    memberBooking: 'hidden',
  },
  {
    id: 'dental-stage-1',
    name: 'Dental Stage 1',
    description: '',
    duration: 15,
    category: 'surgery',
    memberBooking: 'hidden',
    allowedClinicIds: ['bounds-green'],
  },
  {
    id: 'dental-stage-2',
    name: 'Dental Stage 2',
    description: '',
    duration: 15,
    category: 'surgery',
    memberBooking: 'hidden',
    allowedClinicIds: ['bounds-green'],
  },
  {
    id: 'bloodwork',
    name: 'Blood Work',
    description: '',
    duration: 20,
    category: 'diagnostics',
    memberBooking: 'contact-team',
  },
  {
    id: 'blood-pressure-reading',
    name: 'Blood Pressure Reading',
    description: '',
    duration: 10,
    category: 'diagnostics',
    memberBooking: 'hidden',
    allowedClinicIds: ['clifton'],
  },
  {
    id: 'blood-pressure-reading-20',
    name: 'Blood Pressure Reading',
    description: '',
    duration: 20,
    category: 'diagnostics',
    memberBooking: 'hidden',
    allowedClinicIds: ['islington', 'acton', 'london-fields', 'bounds-green'],
  },
  {
    id: 'sedation',
    name: 'Sedation',
    description: '',
    duration: 60,
    category: 'surgery',
    memberBooking: 'hidden',
  },
  {
    id: 'wellness-screen',
    name: 'Wellness Screen',
    description: '',
    duration: 30,
    category: 'wellness',
    memberBooking: 'contact-team',
  },
  {
    id: 'procedure',
    name: 'Procedure',
    description: '',
    duration: 30,
    category: 'surgery',
    memberBooking: 'hidden',
  },
  {
    id: 'fit-to-fly',
    name: 'Fit to Fly',
    description: '',
    duration: 20,
    category: 'admin',
    memberBooking: 'hidden',
  },
  {
    id: 'telemedicine',
    name: 'Telemedicine',
    description: '',
    duration: 20,
    category: 'telemedicine',
    memberBooking: 'hidden',
  },
  {
    id: 'opening-visit',
    name: 'Opening Visit',
    description: '',
    duration: 60,
    category: 'wellness',
    memberBooking: 'hidden',
  },
] as const satisfies AppointmentType[]

export type AppointmentTypeId = (typeof APPOINTMENT_TYPES)[number]['id']

export const appointmentTypeIdSchema = z.enum<AppointmentTypeId[]>(
  APPOINTMENT_TYPES.map((t) => t.id)
)

/** O(1) lookup map from appointment type ID to its definition. */
export const APPOINTMENT_TYPES_BY_ID = keyBy(APPOINTMENT_TYPES, 'id')

/** Human-readable labels for each appointment type category, used in UI pickers. */
export const APPOINTMENT_TYPE_CATEGORY_LABELS: Record<AppointmentTypeCategoryId, string> = {
  wellness: 'Wellness',
  vaccs: 'Vaccines',
  surgery: 'Surgery',
  grooming: 'Grooming',
  admin: 'Admin',
  eol: 'End of Life',
  diagnostics: 'Diagnostics',
  telemedicine: 'Telemedicine',
}

/** Whether a stored id is a current type; older appointments can hold Digitail-era ids such as 'allergy injection'. */
export function isAppointmentTypeId(id: string): id is AppointmentTypeId {
  return Object.hasOwn(APPOINTMENT_TYPES_BY_ID, id)
}

/** The type's name, or the title-cased id for a Digitail-era id. */
export function getAppointmentTypeName(id: string): string {
  return isAppointmentTypeId(id) ? APPOINTMENT_TYPES_BY_ID[id].name : titleCase(id)
}

/**
 * Resolves an appointment type by id or throws `ValidationError`. Accepts a
 * raw `string` so callers coming out of the DB (where `appointmentTypeId`
 * is typed as `string`) don't need to cast at every callsite.
 */
export function requireAppointmentType(id: string): AppointmentType {
  const type: AppointmentType | undefined = APPOINTMENT_TYPES_BY_ID[id as AppointmentTypeId]
  if (!type) throw new ValidationError(`Unknown appointment type: ${id}`)
  return type
}

/** Returns true when the given appointment type can be booked at the given clinic. */
export function isAppointmentTypeAvailableAtClinic(
  appointmentTypeId: AppointmentTypeId,
  clinicId: ClinicId
): boolean {
  const type: AppointmentType | undefined = APPOINTMENT_TYPES_BY_ID[appointmentTypeId]
  if (!type) return false
  return !type.allowedClinicIds || type.allowedClinicIds.includes(clinicId)
}
